import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import GlobalNav from "../components/GlobalNav";
import PillarsGrid from "../components/PillarsGrid";
import AlbumCard from "../components/AlbumCard";
import EventCard from "../components/EventCard";
import BlogCard from "../components/BlogCard";
import Footer from "../components/Footer";
import FeaturedPopup from "../components/FeaturedPopup";
import { InlineForm } from "../components/FormInputs";
import { deltaToPlainText } from "../utils/quillDelta";
import "../styles/buttons.css";
import "./LandingPage.css";

// Ported from Hub.jsx, which used these for the exact same
// featured-post + Latest News grid presentation being moved here.
function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function truncateHTML(description, max = 150) {
  const text = deltaToPlainText(description);
  return text.length > max ? text.substring(0, max) + '…' : text;
}

// Four pillars now live in components/PillarsGrid.jsx (shared with About).

// Fallbacks — shown while /api/home_page is loading, or if a given piece
// comes back empty (e.g. no albums flagged featured yet, no upcoming
// events). Once the backend has real data these are only ever seen briefly
// or not at all.
const PLACEHOLDER_RELEASE = {
  title: "Frequencies",
  artist: "Akwerius",
  format: "Vinyl",
  badge: "New",
  price: "$28",
  remaining: 27,
  total: 100,
};

const PLACEHOLDER_EVENT = {
  month: "Jul",
  day: "12",
  title: "Community Listening",
  time: "7:00 PM",
  location: "Oakland, CA",
};

const PLACEHOLDER_POSTS = [
  { title: "Healing through rhythm", date: "Jun 18", tag: "music" },
  { title: "Sound & wellness camp", date: "Jun 14", tag: "programs" },
  { title: "KWE spotlight", date: "Jun 10", tag: "community" },
];

// Fallback flyers for the "Unity through music" wall — shown until real
// events with an imagepath exist. No image, just the title on a paper card,
// so the wall still looks intentional rather than showing broken images.
const PLACEHOLDER_FLYERS = [
  { id: "p1", title: "Community Listening", imagepath: null },
  { id: "p2", title: "Sound Bath Session", imagepath: null },
  { id: "p3", title: "KWE Spotlight", imagepath: null },
  { id: "p4", title: "Frequencies Release Show", imagepath: null },
  { id: "p5", title: "Rhythm & Rest", imagepath: null },
];

// Slight, varied tilt per flyer so the wall reads as pinned-up rather than
// a grid — repeats if there are more flyers than tilt values.
const FLYER_TILTS = [-4, 3, -2, 5, -3, 2, -5, 4];

// Rotates in the hero, quiet nod to the old page's rotating banner without
// needing image assets or a full carousel.
const MISSION_TAGLINES = [
  "Sound as medicine.",
  "Community as practice.",
  "Healing through rhythm.",
  "Mind, body, community, spirit.",
];

// Real slider images from the original landing page
const SLIDER_IMAGES = [
  { image: "/images/slider/liveinstudio.jpg", caption: "Akwerian Project - Live in Studio @ CCTV (Cambridge, CA)" },
  { image: "/images/slider/Momentum3.png", caption: "Saritah - Momentum @ Spiral Gardens (Berkeley, CA)" },
  { image: "/images/slider/utm8.jpg", caption: "Unity through Music Festival @ Shastice Park (Mt Shasta, CA)" },
];

// All static event flyers from the original site — shown in the
// Unity through Music section. These are local images, not from the DB,
// so they're always available regardless of what's in events.imagepath.
const STATIC_FLYERS = [
  "images/events/unitythroughmusic2020.jpg",
  "images/events/TheJungle-April3rd.png",
  "images/events/UrbanGriots-LaborDay-Party-Flyer.png",
  "images/events/TheJungleNov29.png",
  "images/events/Spontaneous_Celebrations_OpenHouseFlyer.jpg",
  "images/events/Astronomy-Tour-Boston.png",
  "images/events/OCTOBER18TH-FLYER.jpg",
  "images/events/UTM.jpg",
  "images/events/UTM-2018-Poster.jpg",
  "images/events/Wellness_Forum.jpeg",
  "images/events/utm-2022-flyer-front.png",
];

const HERO_ROTATION_MS = 6000;
// returns nothing. Real data comes from GET /registry ("Artist list", per
// routes/public/registry.js) once fetched below.
const PLACEHOLDER_MEMBERS = [
  { id: "m1", name: "Akwerius", role: "Producer · Vocals", photo: null },
  { id: "m2", name: "kwe", role: "Host, MIHU Radio", photo: null },
  { id: "m3", name: "Toots", role: "Multi-instrumentalist", photo: null },
  { id: "m4", name: "Indalah", role: "Sound design", photo: null },
];

// BEST-EFFORT — I don't have routes/public/registry.js's actual response
// shape, so this checks a few likely field names/envelopes rather than
// assuming one. Send me the real file and I'll collapse this to the exact
// shape instead of guessing.
function extractArtistList(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.artists)) return json.artists;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.results)) return json.results;
  return [];
}

function artistToMember(a) {
  // Confirmed from \d members: artistname is the stage/artist name (e.g.
  // "Akwerius"), separate from firstname/lastname — prefer it since that's
  // how these members are presented publicly. `photo` and `roles` are the
  // real column names (there's also a legacy singular `role` column;
  // `roles` looks like the newer one, so it's preferred here).
  const name =
    a.artistname || [a.firstname, a.lastname].filter(Boolean).join(" ");
  return {
    id: a.id,
    slug: a.slug,
    name,
    role: a.roles || a.role || "",
    photo: a.photo || null,
  };
}
// joined with `album_artists` (many-to-many, for collaborations/features).
// `albums` has no `badge` column, so that one's still a TODO.
function releaseToCardProps(release) {
  if (!release) return null;
  return {
    title: release.title,
    artist:
      release.artists && release.artists.length
        ? release.artists.join(", ")
        : "Music Is Healing", // fallback for albums not yet backfilled with artist rows
    format: release.format,
    coverSrc: release.imagepath,
    onCtaClick: release.url ? () => window.open(release.url, "_blank") : undefined,
  };
}

// GET /home_page's `event` row comes straight from the `events` table.
// `day` and `time` are already text (not a timestamp), so this just derives
// EventCard's separate month/day-of-month from `day` via moment — same
// parsing the old landing.js relied on. `time` is used as-is since it's
// already a display string. `location` maps to `city` (no dedicated
// location column exists).
function eventToCardProps(event) {
  if (!event) return null;
  const d = moment(event.day);
  return {
    month: d.isValid() ? d.format("MMM") : undefined,
    day: d.isValid() ? d.format("D") : event.day,
    title: event.title,
    time: event.time,
    location: event.city,
  };
}

// GET /home_page's `posts` come straight from the `blogs` table. `date` is
// a raw timestamp, formatted here to match the "Jun 18" style the
// placeholders used. `author` and `tag` are included for a byline/chip.
function postToCardProps(post) {
  return {
    title: post.title,
    date: post.date ? moment(post.date).format("MMM D") : undefined,
    author: post.author,
    tag: post.tag,
    imageSrc: post.photourl,
  };
}

// Finds the single soonest upcoming event from calendar_events —
// replacing the old events-table-backed `homeData.event`, now that
// calendar_events is the real source of truth (see the events ->
// calendar_events consolidation migration). Has to account for two very
// different kinds of rows:
//   - one-off events (recurrence 'none'): just compare starts_at directly
//   - weekly recurring events (Chi Flow, Capoeira, Freedom Hour Radio):
//     no single stored date, so this computes each one's NEXT occurrence
//     from today, using starts_at only for its time-of-day component
// Whichever candidate (from either group) is soonest overall wins.
function getNextCalendarEvent(events) {
  if (!events || events.length === 0) return null;
  const now = new Date();

  const candidates = events
    .map((e) => {
      if (e.recurrence === "weekly" && e.starts_at != null && e.day_of_week != null) {
        const refTime = new Date(e.starts_at);
        const next = new Date(now);
        next.setHours(refTime.getHours(), refTime.getMinutes(), 0, 0);
        let daysAhead = (e.day_of_week - now.getDay() + 7) % 7;
        // If today's the right weekday but that time already passed,
        // push to next week instead of showing an event that's already over.
        if (daysAhead === 0 && next <= now) daysAhead = 7;
        next.setDate(next.getDate() + daysAhead);
        return { event: e, date: next };
      }
      if (e.recurrence === "none" && e.starts_at) {
        const date = new Date(e.starts_at);
        return date >= now ? { event: e, date } : null;
      }
      return null;
    })
    .filter(Boolean);

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.date - b.date);
  return { ...candidates[0].event, _resolvedDate: candidates[0].date };
}

// calendar_events shape differs from the old events table (starts_at
// instead of separate day/time text columns, location instead of city).
// Events are handled separately now (they drive the Next Event card,
// not the general featured grid), so this only needs to distinguish
// blogs from notes.
function getContentDestination(item) {
  if (item.contentType === 'note') {
    if (item.articlelink) return { href: item.articlelink, external: true };
    return { href: `/notes/${item.id}`, external: false };
  }
  return { href: `/blog/${item.id}`, external: false };
}

function calendarEventToCardProps(event) {
  if (!event) return null;
  const d = event._resolvedDate || (event.starts_at ? new Date(event.starts_at) : null);
  return {
    month: d ? moment(d).format("MMM") : undefined,
    day: d ? moment(d).format("D") : undefined,
    title: event.title,
    time: d ? moment(d).format("h:mm A") : undefined,
    location: event.location,
    imageSrc: event.imagepath || undefined,
    link: event.link || undefined,
  };
}

export default function LandingPage() {
  const [subscribed, setSubscribed] = useState(false);
  const [homeData, setHomeData] = useState(null);
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const [members, setMembers] = useState(null);
  const [featuredContent, setFeaturedContent] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState(null);

  // Admin-only content management -- fetched only when the logged-in
  // user is an admin, matching the same localStorage.user.admin check
  // used everywhere else in this app.
  const [isAdmin, setIsAdmin] = useState(false);
  const [showManager, setShowManager] = useState(false);
  const [heroKey, setHeroKey] = useState(null); // { contentType, id } | null
  const [contentPool, setContentPool] = useState(null); // { blogs, notes, videos }
  const [managerTab, setManagerTab] = useState('blogs');

  const heroSlides = SLIDER_IMAGES;
  const heroImage = heroSlides[heroSlideIndex % heroSlides.length]?.image;
  const heroCaption = heroSlides[heroSlideIndex % heroSlides.length]?.caption;

  useEffect(() => {
    fetch("/api/home_page")
      .then((res) => res.json())
      .then((json) => setHomeData(json))
      .catch((err) => console.error("Error fetching home page data:", err));
  }, []);

  useEffect(() => {
    fetch("/api/registry/featured")
      .then((res) => res.json())
      .then((json) => {
        const featured = extractArtistList(json).map(artistToMember);
        setMembers(featured);
      })
      .catch((err) => console.error("Error fetching featured members:", err));
  }, []);

  useEffect(() => {
    fetch("/api/calendar-events")
      .then((res) => res.json())
      .then((json) => {
        if (json.success) setCalendarEvents(json.events || []);
      })
      .catch((err) => console.error("Error fetching calendar events:", err));
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setTaglineIndex((i) => (i + 1) % MISSION_TAGLINES.length);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const slides = heroSlides.length;
    if (slides < 2) return;
    const id = setInterval(() => {
      setHeroSlideIndex((i) => (i + 1) % slides);
    }, HERO_ROTATION_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroSlides.length]);

  useEffect(() => {
    fetch('/api/featured-content')
      .then((res) => res.json())
      .then((data) => setFeaturedContent(data.featured || []))
      .catch((err) => console.error('Error fetching featured content:', err));
  }, []);

  useEffect(() => {
    fetch('/api/site-content?keys=landing_hero')
      .then((res) => res.json())
      .then((data) => {
        const items = data.content?.landing_hero?.items;
        if (!items) return;
        const byLabel = {};
        items.forEach((i) => { byLabel[i.label] = i.text; });
        if (byLabel.contentType && byLabel.id) {
          setHeroKey({ contentType: byLabel.contentType, id: byLabel.id });
        }
      })
      .catch((err) => console.error('Error fetching hero designation:', err));
  }, []);

  async function setAsHero(contentType, id) {
    try {
      const res = await fetch('/api/site-content/landing_hero', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            { label: 'contentType', text: contentType },
            { label: 'id', text: String(id) },
          ],
        }),
      });
      const data = await res.json();
      if (data.success) setHeroKey({ contentType, id: String(id) });
    } catch (err) {
      console.error('Error setting hero:', err);
    }
  }

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      setIsAdmin(!!user?.admin);
    } catch {
      setIsAdmin(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    fetch('/api/admin/content-pool')
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setContentPool({
            blogs: data.blogs || [],
            notes: data.notes || [],
            videos: data.videos || [],
            events: data.events || [],
            releases: data.releases || [],
          });
        }
      })
      .catch((err) => console.error('Error fetching content pool:', err));
  }, [isAdmin]);

  async function toggleFeatured(table, id, currentValue) {
    try {
      const res = await fetch(`/api/admin/${table}/${id}/featured`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ featured: !currentValue }),
      });
      const data = await res.json();
      if (!data.success) return;

      // Update the pool locally so the checkbox reflects the change
      // immediately, without waiting on a full refetch.
      setContentPool((prev) => ({
        ...prev,
        [table]: prev[table].map((item) =>
          item.id === id ? { ...item, featured: data.featured } : item
        ),
      }));

      // Refetch the public featured list so the actual landing page
      // section updates to match right away too.
      fetch('/api/featured-content')
        .then((res) => res.json())
        .then((d) => setFeaturedContent(d.featured || []))
        .catch((err) => console.error('Error refreshing featured content:', err));
    } catch (err) {
      console.error(`Error toggling featured on ${table}:`, err);
    }
  }

  function handleSubscribe(email) {
    // TODO: POST /subscribe
    console.log("subscribe:", email);
    setSubscribed(true);
  }

  const release = releaseToCardProps(homeData?.release) || PLACEHOLDER_RELEASE;
  const featuredEvents = calendarEvents?.filter((e) => e.featured) || [];
  const nextCalendarEvent = getNextCalendarEvent(
    featuredEvents.length > 0 ? featuredEvents : calendarEvents
  );
  const event = calendarEventToCardProps(nextCalendarEvent) || PLACEHOLDER_EVENT;
  const posts = homeData?.posts?.length
    ? homeData.posts.map(postToCardProps)
    : PLACEHOLDER_POSTS;

  return (
    <div className="landing">
      <GlobalNav activeHref="/" ctaLabel="Join the collective" />
      <FeaturedPopup />

      {/* Hero */}
      <section className="landing-hero landing-hero--no-overlay">
        {heroImage && (
          <img
            key={heroSlideIndex}
            className="landing-hero-bg"
            src={heroImage}
            alt=""
            aria-hidden="true"
          />
        )}
        <div className="landing-hero-inner">
          <div className="landing-hero-text">
            <div className="landing-hero-eyebrow">The MIHU Collective</div>
            {/* <h1 className="landing-hero-title">
              Music as medicine.<br />Community as practice.
            </h1> */}
            <p className="landing-hero-sub">
              A collective of artists, educators, healers, and mediamakers cultivating
              wellness through music for the mind, body, community and spirit.
            </p>
            <div className="landing-hero-ctas">
              <a className="landing-featured-cta" href="/register">Join the collective</a>
              <a className="mihu-btn-secondary-lg" href="/music">Explore music</a>
            </div>
            {/* <p className="landing-hero-tagline" aria-live="polite">
              <span key={taglineIndex} className="landing-hero-tagline-text">
                {MISSION_TAGLINES[taglineIndex]}
              </span>
            </p> */}
          </div>
        </div>

        {/* {heroCaption && (
          <div className="landing-hero-caption">{heroCaption}</div>
        )} */}

        {heroSlides.length > 1 && (
          <div className="landing-hero-dots" role="tablist" aria-label="Background photo">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                className={"landing-hero-dot" + (i === heroSlideIndex ? " active" : "")}
                role="tab"
                aria-selected={i === heroSlideIndex}
                aria-label={`Slide ${i + 1}`}
                onClick={() => setHeroSlideIndex(i)}
              />
            ))}
          </div>
        )}
      </section>


      <div className="landing-body">
        {/* Unity through music — dark two-column layout with flyer carousel */}
        <section className="landing-mission">
          <div className="landing-mission-inner">
            <div className="landing-mission-text">
              <div className="landing-section-label landing-section-label--inverse">
                Unity through music
              </div>
              <h2 className="landing-mission-title">
                Connecting youth with artists and musicians committed to promoting Unity through Music.
              </h2>
              <p className="landing-mission-copy">
                Music Is Healing is a collective of poets, singers, healers and players of
                instruments committed to promoting the message of Unity through Music — through
                seasonal production of music &amp; media releases, merch, and public events
                including workshops, festivals, concerts and mindfulness retreats — promoting
                the four pillars of wellness: Mind, Body, Community and Spirit.
              </p>
        <div className="landing-pillars">
          <PillarsGrid variant="compact" />
        </div>              
              <div className="landing-mission-links">
                <a className="landing-mission-link" href="/events">
                  See what's next <span aria-hidden="true">→</span>
                </a>
                <a className="landing-mission-link" href="/about">
                  Learn our story <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>


          </div>
        </section>

        {/* Four pillars */}
        {/* <div className="landing-section-label">Four pillars</div>

        <a className="landing-story-link" href="/about">
          Learn our story <span aria-hidden="true">→</span>
        </a> */}

        {/* Latest release + next event */}
        <div className="landing-two-col">
          <div>
            <div className="landing-section-label">Latest release</div>
            <AlbumCard {...release} ctaLabel="Pre-order" compact />
            <a href="/shop" className="landing-view-all-link">View all releases →</a>
          </div>
          <div>
            <div className="landing-section-label">Next event</div>
            <EventCard
              {...event}
              ctaLabel={event.link ? "Details" : undefined}
              onCtaClick={event.link ? () => window.open(event.link, "_blank") : undefined}
            />
            <a href="/events" className="landing-view-all-link">View events calendar →</a>
          </div>
        </div>

        {/* From the stream */}
        {/* <div className="landing-section-label">From the stream</div>
        <div className="landing-posts">
          {posts.map((post) => (
            <BlogCard key={post.title} {...post} />
          ))}
        </div> */}

        {/* Featured members — curated via members.featured, per GET /registry/featured */}
        {/* {(members === null || members.length > 0) && (
          <>
            <div className="landing-section-label">Featured members</div>
            <div className="landing-members">
              {(members === null ? PLACEHOLDER_MEMBERS : members).map((m) => {
                const card = (
                  <>
                    <div className="landing-member-photo">
                      {m.photo ? (
                        <img src={m.photo} alt={m.name} />
                      ) : (
                        <span aria-hidden="true">{m.name?.charAt(0)}</span>
                      )}
                    </div>
                    <div className="landing-member-name">{m.name}</div>
                    <div className="landing-member-role">{m.role}</div>
                  </>
                );

                return members === null ? (
                  <div className="landing-member" key={m.id}>
                    {card}
                  </div>
                ) : (
                  <Link className="landing-member" to={`/epk/${m.slug}`} key={m.id}>
                    {card}
                  </Link>
                );
              })}
            </div>
          </>
        )} */}


        {/* Featured content — admin-curated mix of blogs, notes, and
            videos (replaces the old separate blog-featured / episodes
            sections, which pulled everything automatically rather than
            letting an admin choose). Each item renders differently based
            on contentType. */}
        {isAdmin && (
          <div className="landing-manager-toggle-row">
            <button className="landing-manager-toggle" onClick={() => setShowManager((v) => !v)}>
              {showManager ? 'Close featured content manager' : 'Manage featured content'}
            </button>
          </div>
        )}

        {isAdmin && showManager && contentPool && (
          <div className="landing-manager">
            <div className="landing-manager-tabs">
              {['blogs', 'notes', 'videos', 'events', 'releases'].map((t) => (
                <button
                  key={t}
                  className={'landing-manager-tab' + (managerTab === t ? ' active' : '')}
                  onClick={() => setManagerTab(t)}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
            {managerTab === 'events' && (
              <p className="landing-manager-note">
                Featured events show in the "Next event" section, not the general Featured grid below.
              </p>
            )}
            {managerTab === 'releases' && (
              <p className="landing-manager-note">
                A featured release shows in the "Latest release" card, not the general Featured grid below.
              </p>
            )}
            <div className="landing-manager-list">
              {contentPool[managerTab].length === 0 && (
                <p className="landing-manager-empty">Nothing here yet.</p>
              )}
              {contentPool[managerTab].map((item) => (
                <div key={item.id} className="landing-manager-item">
                  <label className="landing-manager-item-label">
                    <input
                      type="checkbox"
                      checked={!!item.featured}
                      onChange={() => toggleFeatured(managerTab, item.id, item.featured)}
                    />
                    <span className="landing-manager-item-title">{item.title || formatDate(item.date)}</span>
                  </label>
                  {item.featured && managerTab !== 'events' && managerTab !== 'releases' && (
                    <button
                      className={
                        'landing-manager-hero-btn' +
                        (heroKey?.contentType === managerTab.slice(0, -1) && String(heroKey?.id) === String(item.id)
                          ? ' active'
                          : '')
                      }
                      onClick={() => setAsHero(managerTab.slice(0, -1), item.id)}
                    >
                      ★ {heroKey?.contentType === managerTab.slice(0, -1) && String(heroKey?.id) === String(item.id) ? 'Hero' : 'Set as hero'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {featuredContent.length > 0 && (() => {
          const heroItem = heroKey
            ? featuredContent.find(
                (i) => i.contentType === heroKey.contentType && String(i.id) === String(heroKey.id)
              )
            : featuredContent[0]; // no explicit hero set yet -- fall back to the first item
          const restItems = featuredContent.filter((i) => i !== heroItem);

          return (
            <>
              <div className="landing-section-label">Featured</div>

              {heroItem && heroItem.contentType !== 'video' && (
                <div className="landing-featured-card">
                  <img
                    className="landing-featured-img"
                    src={heroItem.photourl || '/images/MusicIsHealing-Banner.png'}
                    alt={heroItem.title}
                  />
                  <div className="landing-featured-overlay">
                    <span className="landing-featured-badge">Featured</span>
                    <h2 className="landing-featured-title">{heroItem.title || formatDate(heroItem.date)}</h2>
                    <p className="landing-featured-excerpt">
                      {truncateHTML(heroItem.description || heroItem.short, 200)}
                    </p>
                    {(() => {
                      const dest = getContentDestination(heroItem);
                      if (!dest.href) return null;
                      return (
                        <a
                          href={dest.href}
                          className="landing-featured-cta"
                          target={dest.external ? "_blank" : undefined}
                          rel={dest.external ? "noopener noreferrer" : undefined}
                        >
                          Read more →
                        </a>
                      );
                    })()}
                  </div>
                </div>
              )}

              {heroItem && heroItem.contentType === 'video' && (
                <div className="landing-featured-card landing-featured-card--video">
                  <div className="landing-episode-video">
                    {heroItem.videourl?.includes('youtube') ? (
                      <iframe
                        src={heroItem.videourl}
                        title={heroItem.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video controls>
                        <source src={heroItem.videourl} type="video/mp4" />
                      </video>
                    )}
                  </div>
                  <div className="landing-featured-video-body">
                    <span className="landing-featured-badge">Featured</span>
                    <h2 className="landing-featured-title">{heroItem.title}</h2>
                    <p className="landing-featured-excerpt">{heroItem.description}</p>
                  </div>
                </div>
              )}

              {restItems.length > 0 && (
                <div className="landing-news-grid">
                  {restItems.map((item) => {
                    if (item.contentType === 'video') {
                      return (
                        <div key={`video-${item.id}`} className="landing-news-card landing-news-card--video">
                          <div className="landing-episode-video">
                            {item.videourl?.includes('youtube') ? (
                              <iframe
                                src={item.videourl}
                                title={item.title}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            ) : (
                              <video controls>
                                <source src={item.videourl} type="video/mp4" />
                              </video>
                            )}
                          </div>
                          <div className="landing-news-body">
                            <h3 className="landing-news-title">{item.title}</h3>
                            <p className="landing-news-excerpt">{item.description}</p>
                          </div>
                        </div>
                      );
                    }
                    const dest = getContentDestination(item);
                    return (
                      <div
                        key={`${item.contentType}-${item.id}`}
                        className="landing-news-card"
                        onClick={() => {
                          if (!dest.href) return;
                          if (dest.external) window.open(dest.href, "_blank", "noopener,noreferrer");
                          else window.location.href = dest.href;
                        }}
                      >
                        {item.photourl && (
                          <div className="landing-news-img"><img src={item.photourl} alt={item.title} /></div>
                        )}
                        <div className="landing-news-body">
                          <h3 className="landing-news-title">{item.title || formatDate(item.date)}</h3>
                          <p className="landing-news-excerpt">{truncateHTML(item.description || item.short)}</p>
                          <span className="landing-news-link">
                            Read more →
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}
                    <div className="landing-flyer-wall" role="list">
              {STATIC_FLYERS.map((src, i) => (
                <div
                  key={i}
                  className="landing-flyer"
                  role="listitem"
                  style={{ "--tilt": `${FLYER_TILTS[i % FLYER_TILTS.length]}deg` }}
                >
                  <img src={src} alt={`Event flyer ${i + 1}`} />
                  <span className="landing-flyer-pin" aria-hidden="true" />
                </div>
              ))}
            </div>
      </div>

      <Footer />
    </div>
  );
}